"use client";

import React from "react";
import { Shield, Lock, Database, Cpu, CircleCheck, Terminal } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const securityFeatures = [{
  id: "f1",
  title: "Transport Security",
  description: "All communications between messaging platforms and our orchestration layer are heavily encrypted in transit.",
  icon: Lock,
  specs: [{
    label: "PROTOCOL",
    value: "TLS 1.3"
  }, {
    label: "CIPHER",
    value: "AES-256-GCM"
  }, {
    label: "HANDSHAKE",
    value: "ECDHE-RSA"
  }]
}, {
  id: "f2",
  title: "Ephemeral Processing",
  description: "Conversation context is held only in volatile memory during generation and instantly purged upon completion.",
  icon: Cpu,
  specs: [{
    label: "STORAGE",
    value: "Volatile RAM Only"
  }, {
    label: "RETENTION",
    value: "0 Seconds Post-Gen"
  }, {
    label: "LOGGING",
    value: "Metadata Only (Anonymized)"
  }]
}, {
  id: "f3",
  title: "Data Isolation",
  description: "Each user session operates within a strictly isolated, containerized environment preventing cross-contamination.",
  icon: Database,
  specs: [{
    label: "SANDBOX",
    value: "MicroVM Isolation"
  }, {
    label: "NETWORK",
    value: "Zero-Trust VPC"
  }, {
    label: "TENANCY",
    value: "Strict Single-Tenant Execution"
  }]
}, {
  id: "f4",
  title: "Managed Orchestration",
  description: "Enterprise-grade routing and access controls ensure only authorized requests reach the underlying AI models.",
  icon: Shield,
  specs: [{
    label: "AUTH",
    value: "Cryptographic Token Validation"
  }, {
    label: "RATE_LIMIT",
    value: "Dynamic Token Bucket"
  }, {
    label: "AUDIT",
    value: "Tamper-Evident Ledger"
  }]
}];
export default function SecurityTrust() {
  return <section id="security-trust" className="dark bg-background text-foreground py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: `linear-gradient(to right, var(--color-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)`,
      backgroundSize: '32px 32px'
    }} aria-hidden="true" />
      
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[128px] pointer-events-none" aria-hidden="true" />

      <div className="max-w-7xl mx-auto relative z-10 flex flex-col items-center">
        
        {}
        <div className="text-center max-w-2xl mb-16 space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-default">
            Engineered for Absolute Privacy
          </h2>
          <p className="text-muted-foreground text-lg">
            OceanClaw operates on a zero-knowledge architecture. Your data is your own, processed securely and never stored.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full mb-16">
          {securityFeatures.map((feature, i) => <Card key={feature.id} data-index={i} className="bg-card/40 backdrop-blur-sm border-[0.5px] border-border/50 hover:border-primary/50 transition-colors duration-300 flex flex-col shadow-none">
              <CardHeader className="pb-4">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 border border-primary/20">
                  <feature.icon className="size-5 text-primary" />
                </div>
                <CardTitle className="text-lg font-default">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                  {feature.description}
                </CardDescription>
              </CardContent>
              <CardFooter className="pt-4 border-t border-border/20">
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between font-mono text-xs text-muted-foreground hover:text-primary hover:bg-primary/5 h-8 px-2">
                      <span className="flex items-center gap-2">
                        <Terminal className="size-3" />
                        VIEW_SPECS
                      </span>
                      <span className="opacity-50">[{feature.id.toUpperCase()}]</span>
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent align="start" className="w-72 bg-card/95 backdrop-blur-md border-primary/30 shadow-xl shadow-primary/5">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 border-b border-border/50 pb-2 mb-2">
                        <Terminal className="size-4 text-primary" />
                        <h4 className="font-mono text-sm font-semibold text-foreground">Technical Specifications</h4>
                      </div>
                      <div className="space-y-2">
                        {feature.specs.map((spec, idx) => <div key={idx} data-index={idx} className="flex justify-between items-center text-xs font-mono">
                            <span className="text-muted-foreground">{spec.label}:</span>
                            <span className="text-primary font-medium">{spec.value}</span>
                          </div>)}
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </CardFooter>
            </Card>)}
        </div>

        {}
        <Badge variant="outline" className="bg-background/50 backdrop-blur-md border-primary/30 text-primary px-4 py-1.5 gap-2 font-mono text-sm shadow-sm shadow-primary/10">
          <CircleCheck className="size-4" />
          SYSTEM_VERIFIED_SECURE
        </Badge>

      </div>
    </section>;
}